import { useEffect } from 'react';
import {
	downloadBlobSubscription,
	downloadDocumentSubscription,
	downloadEvents,
	downloadFileSubscription,
	downloadSubscription,
	downloadDataBoxAttachmentSubscription,
	downloadDataBoxMessageSubscription,
	DownloadSubscriptionTypes,
	downloadEPetitionAttachmentSubscription,
	downloadEPetitionSubscription,
	downloadVerificationSubscription,
	downloadForm602AttachmentSubscription,
	downloadPpdfConsentSubscription,
	downloadEPetitionPetitionersSubscription,
	downloadIsssSubscription,
} from './downloadEvents';
import type {
	DownloadBlobSubscription,
	DownloadDocumentSubscription,
	DownloadFileSubscription,
	DownloadSubscription,
	DownloadDataBoxAttachmentSubscription,
	DownloadDataBoxMessageSubscription,
	DownloadEPetitionAttachmentSubscription,
	DownloadEPetitionSubscription,
	DownloadVerificationSubscription,
	DownloadForm602AttachmentSubsription,
	DownloadPpdfConsentSubscription,
	DownloadEpetitionPetitionersSubscription,
	DownloadIsssSubscription,
} from './types';

interface DownloadEventsProps {
	onDownload?: (
		action: DownloadSubscription['type'],
		payload: DownloadSubscription['payload']
	) => void;
	onDocumentDownload?: (
		action: DownloadDocumentSubscription['type'],
		payload: DownloadDocumentSubscription['payload']
	) => void;
	onFileDownload?: (
		action: DownloadFileSubscription['type'],
		payload: DownloadFileSubscription['payload']
	) => void;
	onBlobDownload?: (
		action: DownloadBlobSubscription['type'],
		payload: DownloadBlobSubscription['payload']
	) => void;
	onDataBoxAttachmentDownload?: (
		action: DownloadDataBoxAttachmentSubscription['type'],
		payload: DownloadDataBoxAttachmentSubscription['payload']
	) => void;
	onEPetitionAttachmentDownload?: (
		action: DownloadEPetitionAttachmentSubscription['type'],
		payload: DownloadEPetitionAttachmentSubscription['payload']
	) => void;
	onEPetitionPetitionersDownload?: (
		action: DownloadEpetitionPetitionersSubscription['type'],
		payload: DownloadEpetitionPetitionersSubscription['payload']
	) => void;
	onEPetitionDownload?: (
		action: DownloadEPetitionSubscription['type'],
		payload: DownloadEPetitionSubscription['payload']
	) => void;
	onDataBoxMessageDownload?: (
		action: DownloadDataBoxMessageSubscription['type'],
		payload: DownloadDataBoxMessageSubscription['payload']
	) => void;
	onVerificationDownload?: (
		action: DownloadVerificationSubscription['type'],
		payload: DownloadVerificationSubscription['payload']
	) => void;
	onForm602AttachmentDownload?: (
		action: DownloadForm602AttachmentSubsription['type'],
		payload: DownloadForm602AttachmentSubsription['payload']
	) => void;
	onPpdfConsentDownload?: (
		action: DownloadPpdfConsentSubscription['type'],
		payload: DownloadPpdfConsentSubscription['payload']
	) => void;
	onIsssDownload?: (
		action: DownloadIsssSubscription['type'],
		payload: DownloadIsssSubscription['payload']
	) => void;
}

export const useDownloadEvents = ({
	onDownload,
	onDocumentDownload,
	onFileDownload,
	onBlobDownload,
	onDataBoxAttachmentDownload,
	onEPetitionAttachmentDownload,
	onEPetitionPetitionersDownload,
	onEPetitionDownload,
	onDataBoxMessageDownload,
	onVerificationDownload,
	onForm602AttachmentDownload,
	onPpdfConsentDownload,
	onIsssDownload,
}: DownloadEventsProps = {}) => {
	useEffect(() => {
		const download =
			onDownload &&
			downloadEvents.subscribe(DownloadSubscriptionTypes.download, onDownload);
		const downloadDocument =
			onDocumentDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadDocument,
				onDocumentDownload
			);
		const downloadFile =
			onFileDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadFile,
				onFileDownload
			);
		const downloadBlob =
			onBlobDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadBlob,
				onBlobDownload
			);
		const downloadDataBoxAttachment =
			onDataBoxAttachmentDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadDataBoxAttachment,
				onDataBoxAttachmentDownload
			);
		const downloadEPetitionAttachment =
			onEPetitionAttachmentDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadEPetitionAttachment,
				onEPetitionAttachmentDownload
			);

		const downloadEPetitionPetitioners =
			onEPetitionPetitionersDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadEPetitionPetitioners,
				onEPetitionPetitionersDownload
			);
		const downloadEPetition =
			onEPetitionDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadEPetition,
				onEPetitionDownload
			);
		const downloadDataBoxMessage =
			onDataBoxMessageDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadDataBoxMessage,
				onDataBoxMessageDownload
			);
		const downloadVerification =
			onVerificationDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadVerification,
				onVerificationDownload
			);
		const downloadForm602Attachment =
			onForm602AttachmentDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadForm602Attachment,
				onForm602AttachmentDownload
			);
		const downloadPpdfConsent =
			onPpdfConsentDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadPpdfConsent,
				onPpdfConsentDownload
			);
		const downloadIsss =
			onIsssDownload &&
			downloadEvents.subscribe(
				DownloadSubscriptionTypes.downloadIsss,
				onIsssDownload
			);

		return () => {
			download?.unSubscribe();
			downloadDocument?.unSubscribe();
			downloadFile?.unSubscribe();
			downloadBlob?.unSubscribe();
			downloadDataBoxAttachment?.unSubscribe();
			downloadEPetitionAttachment?.unSubscribe();
			downloadEPetitionPetitioners?.unSubscribe();
			downloadEPetition?.unSubscribe();
			downloadDataBoxMessage?.unSubscribe();
			downloadVerification?.unSubscribe();
			downloadForm602Attachment?.unSubscribe();
			downloadPpdfConsent?.unSubscribe();
			downloadIsss?.unSubscribe();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	const download = (payload: DownloadSubscription['payload']) => {
		downloadEvents.publish(downloadSubscription(payload));
	};

	const downloadFile = (payload: DownloadFileSubscription['payload']) => {
		downloadEvents.publish(downloadFileSubscription(payload));
	};

	const downloadDocument = (
		payload: DownloadDocumentSubscription['payload']
	) => {
		downloadEvents.publish(downloadDocumentSubscription(payload));
	};

	const downloadBlob = (payload: DownloadBlobSubscription['payload']) => {
		downloadEvents.publish(downloadBlobSubscription(payload));
	};

	const downloadDataBoxAttachment = (
		payload: DownloadDataBoxAttachmentSubscription['payload']
	) => {
		downloadEvents.publish(downloadDataBoxAttachmentSubscription(payload));
	};

	const downloadEPetitionAttachment = (
		payload: DownloadEPetitionAttachmentSubscription['payload']
	) => {
		downloadEvents.publish(downloadEPetitionAttachmentSubscription(payload));
	};

	const downloadEPetitionPetitioners = (
		payload: DownloadEpetitionPetitionersSubscription['payload']
	) => {
		downloadEvents.publish(downloadEPetitionPetitionersSubscription(payload));
	};

	const downloadEPetition = (
		payload: DownloadEPetitionSubscription['payload']
	) => {
		downloadEvents.publish(downloadEPetitionSubscription(payload));
	};

	const downloadDataBoxMessage = (
		payload: DownloadDataBoxMessageSubscription['payload']
	) => {
		downloadEvents.publish(downloadDataBoxMessageSubscription(payload));
	};

	const downloadVerification = (
		payload: DownloadVerificationSubscription['payload']
	) => {
		downloadEvents.publish(downloadVerificationSubscription(payload));
	};

	const downloadForm602Attachment = (
		payload: DownloadForm602AttachmentSubsription['payload']
	) => {
		downloadEvents.publish(downloadForm602AttachmentSubscription(payload));
	};

	const downloadPpdfConsent = (
		payload: DownloadPpdfConsentSubscription['payload']
	) => {
		downloadEvents.publish(downloadPpdfConsentSubscription(payload));
	};

	const downloadIsss = (payload: DownloadIsssSubscription['payload']) => {
		downloadEvents.publish(downloadIsssSubscription(payload));
	};

	return {
		download,
		downloadFile,
		downloadDocument,
		downloadBlob,
		downloadDataBoxAttachment,
		downloadEPetitionAttachment,
		downloadEPetitionPetitioners,
		downloadEPetition,
		downloadDataBoxMessage,
		downloadVerification,
		downloadForm602Attachment,
		downloadPpdfConsent,
		downloadIsss,
	};
};
